import CAF from 'caf';

class FontLoadingTimeoutError extends Error {}

/**
 * Return a promise that resolves when font loading has finished.
 * For browsers without first class support, fallback to window.onload
 *
 * If font loading takes too long, the function will resolve anyway after 2500ms.
 *
 * Using window.onload can still present a race condition but is less likely
 * to resolve too early than simply resolving immediately.
 *
 * Use this promise to postpone execution of code that relies on accurate font rendering.
 * For example, the HTML measuring functions in app/assets/components/common/size_helpers.js
 * require fonts to be loaded before they are able to accurately measure elements.
 *
 * @return {Promise} A Promise that resolves when fonts are finished loading.
 */
const getFontLoadingPromise = () => {
  const timeoutToken = CAF.timeout(
    2500,
    new FontLoadingTimeoutError('Font loading took too long, resolving anyway')
  );

  const fontLoading = CAF(function* main(signal) {
    if (document.fonts && document.fonts.ready instanceof Promise) {
      yield document.fonts.ready;
      return true;
    }
    return new Promise(resolve => {
      window.onload = () => resolve(true);
    });
  });

  return fontLoading(timeoutToken).catch(error => {
    // If the loading timed out, resolve anyway
    if (error instanceof FontLoadingTimeoutError) {
      return true;
    }
    throw error;
  });
};

export { getFontLoadingPromise };
